import { cloneDeep } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { TextMeasurer } from '@/utils/text-kit';
import {
    LayerType,
    MaterialIdsType,
    TextWritingType,
    type ILayerProps,
    type IVectorLayerProps,
    type ListStyleStruct,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, updateLayer } from '../utils';

export interface IListText {
    /**
     * 应用列表文本
     *
     * @param layerID - 图层 ID
     * @param data - 列表样式数据
     */
    applyListText(layerID: string, data: ListStyleStruct): ILayerProps | void;

    /**
     * 取消设置列表文本
     *
     * @param layerID - 图层 ID
     */
    cancelListText(layerID: string): ILayerProps | void;
}

export class ListText implements IListText {
    constructor(private framework: IFramework) {}

    private calcLayout(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const layerData = layer.data as IVectorLayerProps;
        const textInfo = layerData.textInfo as VectorTextInfo;

        const { writingMode } = textInfo;
        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: isHorizontal,
            keepConstantHeight: !isHorizontal,
            preservePadding: textInfo.padding,
        });

        const nextLayerWidth = Math.max(layerWidth, textSize.width);
        const nextLayerHeight = Math.max(layerHeight, textSize.height);

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    private set(layerID: string, data: ListStyleStruct | null) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo || textInfo.listStyle === data?.style) return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, listStyle: data ? data.style : '' };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        // 重新计算布局
        const { width: nextLayerWidth, height: nextLayerHeight } = this.calcLayout(nextLayer);
        nextLayer.width = nextLayerWidth;
        nextLayer.height = nextLayerHeight;

        // 更新素材 id
        const nextMaterialIdMap = nextLayer.materialIds ? cloneDeep(nextLayer.materialIds) : {};
        const nextTextListMaterials = data ? [data.id] : [];
        nextMaterialIdMap[MaterialIdsType.TEXT_LIST] = nextTextListMaterials;
        nextLayer.materialIds = nextMaterialIdMap;

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].listStyle);

        return nextLayer;
    }

    applyListText(layerID: string, data: ListStyleStruct) {
        return this.set(layerID, data);
    }

    cancelListText(layerID: string) {
        return this.set(layerID, null);
    }
}
